import { useEffect, useMemo, useRef, useState } from 'react';
import { Paperclip, Pencil, Trash2 } from 'lucide-react';
import type { CommentAttachment, TaskActivity, TaskComment } from '../../../lib/tasksApi';
import { uploadTaskFile } from '../../../lib/tasksApi';
import { useUploadQueue } from '../../../hooks/useUploadQueue';
import { UploadChipList } from '../../ui/UploadProgress';
import { RelativeTime } from '../../ui/RelativeTime';
import { describeActivity, isShownByCommentList } from '../../../lib/taskActivityText';
import {
  useAddComment,
  useDeleteComment,
  useDeleteCommentAttachment,
  useTimeline,
  useUpdateComment,
} from '../../../hooks/useTaskDetail';
import { CommentAttachments } from '../../ui/CommentAttachments';
import { useConfirm } from '../../ui/ConfirmProvider';
import { useAuthStore } from '../../../store/useAuthStore';
import {
  canEditComment,
  canRemoveComment,
  removeAttachmentPrompt,
  removeCommentPrompt,
} from '../../../lib/commentRemoval';
import { ActivityDetail } from './ActivityDetail';
import { Avatar, MeAvatar } from './Avatar';
import { CommentComposerBox } from './CommentComposerBox';
import { MarkdownView } from './MarkdownView';

/**
 * CLE-182 — comments and change history for the task detail page.
 *
 * Two separate sections, fed by the one `/timeline` request:
 *
 *   Comentarii — the discussion. Markdown, avatars, inline images and file
 *     attachments, edit and delete (the author only — the whole comment
 *     or one file), with the composer underneath.
 *   Activitate — what actually changed on the ticket: status, priority,
 *     assignee, department, estimate, logged time, due date, labels, title,
 *     description, relations, project.
 *
 * They used to be one chronological list, and that was the problem: "X a
 * adăugat un comentariu", with the comment quoted under it, appeared directly
 * beneath the comment itself, so the change history was mostly duplicated
 * discussion and the edits nobody wanted to miss were the hardest thing to
 * find. The split is presentation only — the audit rows are untouched, and
 * {@link isShownByCommentList} is the single place that decides which rows the
 * comment section already covers.
 */

/** Section heading with the row count beside it, shared by both sections. */
function SectionHeading({ title, count }: { title: string; count: number }) {
  return (
    <h2 className="mb-3 flex items-center gap-2 text-[13px] font-semibold text-gray-500">
      {title}
      {count > 0 && (
        <span className="rounded-full bg-gray-200 px-1.5 py-px text-[11px] font-medium text-gray-500">
          {count}
        </span>
      )}
    </h2>
  );
}

export function DetailActivity({ taskId }: { taskId: string }) {
  const { data: timeline, isLoading } = useTimeline(taskId);
  const deleteComment = useDeleteComment(taskId);
  const deleteAttachment = useDeleteCommentAttachment(taskId);
  const updateComment = useUpdateComment(taskId);
  const confirm = useConfirm();
  const viewer = useAuthStore((s) => s.user);
  const removeError = deleteComment.error ?? deleteAttachment.error;

  // BUG-17: a file dropped anywhere on the page outside a drop zone used to be
  // opened by the browser (navigating away from the task). Swallow those
  // drops; the composer and each own comment handle theirs before this runs.
  useEffect(() => {
    const block = (e: DragEvent) => {
      if (e.dataTransfer?.types.includes('Files')) e.preventDefault();
    };
    window.addEventListener('dragover', block);
    window.addEventListener('drop', block);
    return () => {
      window.removeEventListener('dragover', block);
      window.removeEventListener('drop', block);
    };
  }, []);

  async function removeComment(comment: TaskComment) {
    if (await confirm(removeCommentPrompt(comment))) deleteComment.mutate(comment.id);
  }

  async function removeAttachment(comment: TaskComment, index: number, name: string) {
    if (await confirm(removeAttachmentPrompt(comment, name))) {
      deleteAttachment.mutate({ commentId: comment.id, index, name });
    }
  }

  // One request, split two ways. Both keep the server's chronological order,
  // oldest first, so the history reads top to bottom from "a creat sarcina".
  const { comments, changes } = useMemo(() => {
    const items = timeline ?? [];
    return {
      comments: items.flatMap((i) => (i.kind === 'comment' ? [i.data] : [])),
      changes: items.flatMap((i) =>
        i.kind === 'activity' && !isShownByCommentList(i.data) ? [i.data] : [],
      ),
    };
  }, [timeline]);

  return (
    <>
      <section className="mt-8">
        <SectionHeading title="Comentarii" count={comments.length} />

        {isLoading ? (
          <p className="text-[13px] text-gray-400">Se încarcă comentariile…</p>
        ) : comments.length === 0 ? (
          <p className="text-[13px] text-gray-400">Încă niciun comentariu.</p>
        ) : (
          <ul className="space-y-3">
            {comments.map((comment) => (
              <CommentRow
                key={comment.id}
                taskId={taskId}
                comment={comment}
                canEdit={canEditComment(viewer, comment.authorId)}
                canRemove={canRemoveComment(viewer, comment.authorId)}
                onDelete={() => void removeComment(comment)}
                onRemoveAttachment={(index, name) => void removeAttachment(comment, index, name)}
                onEdit={(id, content) => updateComment.mutate({ commentId: id, content })}
                onAddAttachments={(added) =>
                  updateComment.mutateAsync({
                    commentId: comment.id,
                    attachments: [...(comment.attachments ?? []), ...added],
                  })
                }
              />
            ))}
          </ul>
        )}
        {removeError && (
          <p className="mt-2 text-[12px] text-red-600">
            Nu s-a putut șterge: {removeError.message}
          </p>
        )}

        <CommentComposer taskId={taskId} />
      </section>

      <section className="mt-8 border-t border-gray-200 pt-6">
        <SectionHeading title="Activitate" count={changes.length} />

        {isLoading ? (
          <p className="text-[13px] text-gray-400">Se încarcă activitatea…</p>
        ) : changes.length === 0 ? (
          <p className="text-[13px] text-gray-400">Nicio modificare înregistrată încă.</p>
        ) : (
          <ul className="space-y-3">
            {changes.map((activity) => (
              <ActivityRow key={activity.id} activity={activity} />
            ))}
          </ul>
        )}
      </section>
    </>
  );
}

/** One line of change history: who, what changed, when. */
function ActivityRow({ activity }: { activity: TaskActivity }) {
  const { text, detail } = describeActivity(activity);
  return (
    <li className="flex gap-2 pl-1 text-[12.5px] text-gray-400">
      <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-gray-300" />
      <div className="min-w-0 flex-1">
        <span>
          {activity.user?.name ?? 'Cineva'} {text}
        </span>
        <span className="text-gray-300"> · </span>
        <RelativeTime iso={activity.createdAt} />
        {detail && <ActivityDetail detail={detail} />}
      </div>
    </li>
  );
}

function CommentRow({
  taskId,
  comment: c,
  canEdit,
  canRemove,
  onDelete,
  onRemoveAttachment,
  onEdit,
  onAddAttachments,
}: {
  taskId: string;
  comment: TaskComment;
  canEdit: boolean;
  canRemove: boolean;
  onDelete: () => void;
  onRemoveAttachment: (index: number, name: string) => void;
  onEdit: (id: string, content: string) => void;
  onAddAttachments: (added: CommentAttachment[]) => Promise<unknown>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  // BUG-17: the author can add files to an existing comment — the paperclip
  // next to the pencil, or a drop onto the comment.
  const fileRef = useRef<HTMLInputElement>(null);
  const { uploads, isUploading, run } = useUploadQueue();
  const [attachError, setAttachError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  async function attachFiles(files: FileList | File[]) {
    const list = Array.from(files);
    if (list.length === 0 || !canEdit) return;
    setAttachError(null);
    const added: CommentAttachment[] = [];
    try {
      for (const f of list) {
        added.push(await run(f, (onProgress) => uploadTaskFile(taskId, f, onProgress)));
      }
    } catch (err) {
      setAttachError(err instanceof Error ? err.message : 'Încărcarea fișierului a eșuat.');
    }
    if (added.length === 0) return;
    try {
      await onAddAttachments(added);
    } catch (err) {
      setAttachError(err instanceof Error ? err.message : 'Atașarea la comentariu a eșuat.');
    }
  }

  function commitEdit() {
    const next = draft.trim();
    setEditing(false);
    if (next && next !== c.content) onEdit(c.id, next);
  }

  return (
    <li className="flex gap-2.5">
      <Avatar name={c.author?.name ?? null} size={26} />
      <div
        className={`min-w-0 flex-1 rounded-md border bg-card px-3 py-2 ${
          dragOver ? 'border-brand-600 ring-1 ring-brand-600' : 'border-gray-200'
        }`}
        onDragOver={
          canEdit
            ? (e) => {
                if (!e.dataTransfer.types.includes('Files')) return;
                e.preventDefault();
                setDragOver(true);
              }
            : undefined
        }
        onDragLeave={canEdit ? () => setDragOver(false) : undefined}
        onDrop={
          canEdit
            ? (e) => {
                setDragOver(false);
                if (!e.dataTransfer.files.length) return;
                e.preventDefault();
                void attachFiles(e.dataTransfer.files);
              }
            : undefined
        }
      >
        <div className="mb-1 flex items-center justify-between">
          <span className="text-[13px] font-medium text-gray-800">
            {c.author?.name ?? 'Necunoscut'}
          </span>
          <div className="flex items-center gap-2">
            <RelativeTime iso={c.createdAt} className="text-[11px] text-gray-400" />
            {canEdit && !editing && (
              <>
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={isUploading}
                  className="text-gray-400 hover:text-gray-800 disabled:opacity-40"
                  title="Atașează fișiere la comentariu"
                  aria-label="Atașează fișiere la comentariu"
                >
                  <Paperclip size={13} />
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  multiple
                  hidden
                  onChange={(e) => {
                    if (e.target.files?.length) void attachFiles(e.target.files);
                    e.target.value = '';
                  }}
                />
              </>
            )}
            {canEdit && !editing && (
              <button
                type="button"
                onClick={() => {
                  setDraft(c.content);
                  setEditing(true);
                }}
                className="text-gray-400 hover:text-gray-800"
                aria-label="Editează comentariul"
              >
                <Pencil size={13} />
              </button>
            )}
            {canRemove && (
              <button
                type="button"
                onClick={onDelete}
                className="text-gray-400 hover:text-red-500"
                title="Șterge comentariul"
                aria-label="Șterge comentariul"
              >
                <Trash2 size={13} />
              </button>
            )}
          </div>
        </div>
        {editing ? (
          <div>
            <textarea
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setEditing(false);
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  commitEdit();
                }
              }}
              rows={Math.max(2, draft.split('\n').length)}
              className="block w-full resize-y rounded border border-gray-200 bg-transparent px-2 py-1.5 text-[13px] text-gray-800 focus:border-brand-600 focus:outline-none"
            />
            <div className="mt-1.5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setEditing(false)}
                className="rounded px-2 py-0.5 text-[12px] text-gray-500 hover:bg-gray-200"
              >
                Anulează
              </button>
              <button
                type="button"
                onClick={commitEdit}
                className="rounded bg-brand-600 px-2.5 py-0.5 text-[12px] font-medium text-fixed-white hover:bg-brand-700"
              >
                Salvează
              </button>
            </div>
          </div>
        ) : (
          <>
            {c.content && <MarkdownView content={c.content} />}
            <CommentAttachments
              attachments={c.attachments ?? []}
              onRemove={canRemove ? (index, a) => onRemoveAttachment(index, a.name) : undefined}
            />
            {uploads.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                <UploadChipList uploads={uploads} />
              </div>
            )}
            {attachError && <p className="mt-1 text-[11px] text-red-500">{attachError}</p>}
          </>
        )}
      </div>
    </li>
  );
}

function CommentComposer({ taskId }: { taskId: string }) {
  const addComment = useAddComment(taskId);
  return (
    <div className="mt-5 flex gap-2.5">
      <div className="hidden sm:block">
        <MeAvatar size={26} />
      </div>
      <CommentComposerBox
        taskId={taskId}
        busy={addComment.isPending}
        onSubmit={(content, attachments) =>
          addComment.mutateAsync({
            content,
            attachments: attachments.length ? attachments : undefined,
          })
        }
      />
    </div>
  );
}
